import React, { useEffect, useRef, useState } from "react";
import { Bar, BarChart, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
// @ts-ignore
import { saveAs } from 'file-saver';

import './Graph.css';
import { GraphModel } from "../../models/GraphModel";
import { useLocation, useNavigate } from "react-router-dom";
import { GraphResultModel } from "../../models/GraphResultModel ";
import html2canvas from "html2canvas";
import useBreakpoints from "../../core/hooks/useBreakpoints";


const completeResultsTitle = 'Diagnóstico de madurez 10 subsistemas';
const partialResultsTitle = 'Diagnóstico de madurez 3 áreas';

const getCompleteResults = (surveyData: number[]) => {
    let completeResults: GraphResultModel[] = [
        {
            name: 'Modelo de negocio',
            value: Number(((surveyData[0] + surveyData[1]) / 2).toFixed(2)),
            color: '#4f81bd'
        },
        {
            name: 'Red de contactos',
            value: Number(((surveyData[2] + surveyData[3] + surveyData[4]) / 3).toFixed(2)),
            color: '#c0504d'
        },
        {
            name: 'Estructura organizacional',
            value: Number(((surveyData[5] + surveyData[6] + surveyData[7] + surveyData[8]) / 4).toFixed(2)),
            color: '#9bbb59'
        },
        {
            name: 'Procesos',
            value: Number(((surveyData[9] + surveyData[10] + surveyData[11] + surveyData[12]) / 4).toFixed(2)),
            color: '#8064a2'
        },
        {
            name: 'Producto',
            value: Number(((surveyData[13] + surveyData[14]) / 2).toFixed(2)),
            color: '#4bacc6'
        },
        {
            name: 'Plataforma/sistema',
            value: Number(((surveyData[15] + surveyData[16]) / 2).toFixed(2)),
            color: '#f79646'
        },
        {
            name: 'Servicio',
            value: Number(((surveyData[16] + surveyData[17] + surveyData[18]) / 3).toFixed(2)),
            color: '#84a7d1'
        },
        {
            name: 'Canal',
            value: Number(((surveyData[18] + surveyData[19] + surveyData[20] + surveyData[21]) / 4).toFixed(2)),
            color: '#d38582'
        },
        {
            name: 'Marca',
            value: Number(((surveyData[22] + surveyData[23] + surveyData[24]) / 3).toFixed(2)),
            color: '#b9cf8b'
        },
        {
            name: 'Engagement',
            value: Number(((surveyData[25] + surveyData[26]) / 2).toFixed(2)),
            color: '#a693be'
        }
    ];

    return completeResults;
}

const getPartialResults = (completeResults: any) => {
    const partialResults: GraphResultModel[] = [
        {
            name: 'Configuración',
            value: Number(((completeResults[0].value + completeResults[1].value + completeResults[2].value + completeResults[3].value) / 4).toFixed(2)),
            color: "#a693be"
        },
        {
            name: 'Oferta',
            value: Number(((completeResults[4].value + completeResults[5].value) / 2).toFixed(2)),
            color: "#b9cf8b"
        },
        {
            name: 'Experiencia',
            value: Number(((completeResults[6].value + completeResults[7].value + completeResults[8].value + completeResults[9].value) / 4).toFixed(2)),
            color: "#d38582"
        }
    ];

    return partialResults;
}

const Graph = () => {
    const navigate = useNavigate();

    const { isXs, isSm, isMd, isLg, active } = useBreakpoints();

    const chartRef = useRef(null);
    const [graphData, setGraphData] = useState<GraphResultModel[]>([]);
    const [title, setTitle] = useState<string>('');

    const location = useLocation();
    const params: GraphModel = location.state;

    useEffect(() => {
        if (params) {
            const completeResults = getCompleteResults(params.data.answers);
            const partialResults = getPartialResults(completeResults);


            if (params.type === 'partial') {
                setTitle(partialResultsTitle);
                setGraphData(partialResults);
            }
            else {
                setTitle(completeResultsTitle);
                setGraphData(completeResults);
            }

            // setShowChart(true);
        }
        else {
            navigate('/encuesta', { replace: true });
        }
    }, [navigate, params]);

    const showContactDataComponent = () => {
        navigate(
            '/datos-contacto',
            {
                state: params
            }
        );
    }

    const downloadGraph = async () => {
        // @ts-ignore
        let chartSVG: HTMLElement = chartRef.current.container;

        (await html2canvas(chartSVG)).toBlob((blob) => {
            saveAs(blob, `${(params.type === 'partial' ? partialResultsTitle : completeResultsTitle)}.png`);
        });
    }

    const getGraphTitleXValue = () => {
        if (isXs) {
            return '25vw';
        }
        if (isSm) {
            return '20vw';
        }
        if (isMd) {
            return '15vw';
        }

        return '10vw';
    }

    const printGraphTitle = () => {
        if (isXs) {
            return <>
                <text x={getGraphTitleXValue()} y={50} fill="black" dominantBaseline="central">
                    <tspan className="graph-title">
                        {(params?.type === 'partial' ? title : title.substring(0, 22))}
                    </tspan>
                </text>
                <text x={getGraphTitleXValue()} y={75} fill="black" dominantBaseline="central">
                    <tspan className="graph-title">
                        {(params?.type === 'partial' ? '' : title.substring(23, 37))}
                    </tspan>
                </text>
            </>
        }
        return <text x={getGraphTitleXValue()} y={50} fill="black" dominantBaseline="central">
            <tspan className="graph-title">{title}</tspan>
        </text>
    }

    return (
        <>
            <div className="title-container">
                <h1 className="text-center">Resultados de encuesta</h1>
                <button className="btn btn-custom" onClick={() => downloadGraph()}>Descargar</button>
                {(params?.type === 'partial' && !params?.from) ?
                    <button className="btn btn-custom" onClick={() => showContactDataComponent()}>Ver resultados completos</button> : ''
                }
            </div>
            <ResponsiveContainer className="graph-container">
                <BarChart
                    ref={chartRef}
                    width={1000}
                    height={500}
                    data={graphData}
                    margin={{
                        top: 5,
                        right: 30,
                        left: 20,
                        bottom: 5,
                    }}
                >
                    {printGraphTitle()}
                    <XAxis dataKey="name" angle={45} tickMargin={70} height={160} />
                    <YAxis padding={{ top: 100 }} />
                    <Tooltip cursor={{ fill: 'transparent' }} />
                    {/* <Legend /> */}
                    <Bar dataKey="value" fill={"value"} >
                        <LabelList dataKey='value' position='top' />
                        {
                            graphData.map((value, index) => <Cell key={index} fill={value.color} />)
                        }
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </>
    );
}

export default Graph;